import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { ExtendedAttribute } from 'app/library/extended-attributes/models/extended-attribute';
import { ExtendedAttributeFilter } from 'app/library/extended-attributes/models/extended-attribute-filter.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import * as moment from 'moment';
import { Moment } from 'moment';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-date-filter',
    templateUrl: './date-filter.component.html',
    styleUrls: ['./date-filter.component.scss'],
    animations: fuseAnimations,
})
export class DateFilterComponent extends UnsubscribeOnDestroy implements OnChanges, OnInit {
    @Input()
    userAttribute: ExtendedAttribute;

    @Input()
    userAttributeFilter: ExtendedAttributeFilter;

    @Output()
    addedToList = new EventEmitter<IChoosenFilterInterface[]>();

    list: IChoosenFilterInterface[] = [];
    dateRange = new FormGroup({
        startDate: new FormControl<Moment | null>(null),
        endDate: new FormControl<Moment | null>(null),
    });
    minDate: Moment;
    maxDate: Moment;
    isDatePickerVisible: boolean = false;

    private _rangePickerOpen: boolean = false;

    onRangePickerOpened(): void {
        this._rangePickerOpen = true;
    }

    onRangePickerClosed(): void {
        this._rangePickerOpen = false;
        this._emitCompletedRangeIfChanged();
        this._initValues();
    }

    private _emitCompletedRangeIfChanged(): void {
        const value = this.dateRange.getRawValue() as { startDate: Moment | null; endDate: Moment | null };
        if (!value?.startDate || !value?.endDate || !this.userAttribute?.id) {
            return;
        }

        const existing = this.list.find((f) => f.entity.id === this.userAttribute.id);
        if (
            existing &&
            moment(existing.rangeValue1).isSame(value.startDate, 'day') &&
            moment(existing.rangeValue2).isSame(value.endDate, 'day')
        ) {
            return;
        }

        this.addedToList.emit([
            ...this.list.filter((item) => item.entity.id !== this.userAttribute.id),
            {
                entity: { id: this.userAttribute.id, type: 'ExtendedAttribute' },
                valueToShow: `${this._translateService.instant(this.userAttribute.translationKey)}: ${moment(value.startDate).format(
                    'YYYY-MM-DD'.replace(/-/g, '/')
                )} - ${moment(value.endDate).format('YYYY-MM-DD'.replace(/-/g, '/'))}`,
                attributeName: this.userAttribute.name,
                attributeId: this.userAttribute.id,
                isRange: true,
                rangeValue1: moment(value.startDate).format(),
                rangeValue2: moment(value.endDate).format(),
            },
        ]);
    }

    private _initValues(): void {
        if (this.userAttributeFilter?.min && this.userAttributeFilter?.max) {
            this.isDatePickerVisible = true;

            this.minDate = moment(this.userAttributeFilter.min);
            this.maxDate = moment(this.userAttributeFilter.max);
        } else {
            this.isDatePickerVisible = false;
        }

        if (this._rangePickerOpen) {
            return;
        }

        if (!this.userAttribute?.id) {
            return;
        }

        for (const filter of this.list) {
            if (filter.entity.id !== this.userAttribute.id) {
                continue;
            }

            const selected = {
                startDate: moment(filter.rangeValue1),
                endDate: moment(filter.rangeValue2),
            };

            this.dateRange.setValue(selected, { emitEvent: false });
            break;
        }
    }

    constructor(private _allUsersService: AllUsersApiService, private _translateService: TranslateService) {
        super();
    }

    ngOnInit(): void {
        this._allUsersService.filtersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            this.list = filters;
            this._initValues();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['userAttribute'] || changes['userAttributeFilter']) {
            this._initValues();
        }
    }
}
